/**
 * Synthetic ids for pending extract rows (VALE-977).
 *
 * `InventoryItem.id` and `Contact.id` are both required `number`s, and the
 * item-list + drawer components key, look up and commit by them. A pending row
 * has no row behind it, so review has to supply an identity — and the identity
 * it already has is its **position in the preview array**.
 *
 * That is not an arbitrary choice: the ai-import surface keys its entire edit
 * overlay off the original preview index (`editedData[originalIndex]`), and
 * `remapEntitlementIndices` / `seedHolderExclusions` depend on those positions
 * too. Encoding the index *is* binding to the identity that surface already
 * owns, rather than minting a second one that could drift from it.
 *
 * Encoded negative so a synthetic id can never be mistaken for — or collide
 * with — a real persisted id, which is always positive. Index 0 maps to -1,
 * so there is no ambiguous zero.
 *
 * **The negative encoding is a convention shared with the rest of the app**:
 * `lib/utils/persisted-record.ts` is the other half, and it is what a component
 * holding one of these rows should ask before doing anything *because* the
 * record has an id. A component must not import from here — the encoding is
 * this surface's business, "does it exist" is everyone's.
 */

/** The pending row at `index` → the id its list row and drawer are keyed by. */
export function pendingIdForIndex(index: number): number {
  return -(index + 1);
}

/** The id a row was keyed with → its index in the preview array. */
export function indexForPendingId(id: number): number {
  return -id - 1;
}

/** Whether an id was minted by {@link pendingIdForIndex} rather than the API. */
export function isPendingId(id: number): boolean {
  return id < 0;
}

/**
 * **Whether the REVIEWER added this row, rather than the extraction** (VALE-1617).
 *
 * Set where the row is created (`useContactHandlers.createContact`,
 * `useInventoryHandlers.createInventoryItem`) and carried through the pending
 * adapters, which spread the extracted record whole.
 *
 * It does not change whether the row is a create — it is one, and its badge and
 * change ink say so truthfully. It changes whether there is anything to
 * REVIEW: nobody proposed these values, so the hover card offering to accept or
 * reject one is an affordance for a decision that does not exist. See
 * `AiReviewFieldMarkingOptions.offersReview`.
 */
export function isReviewerAdded(row: unknown): boolean {
  return (row as { addedByReviewer?: boolean })?.addedByReviewer === true;
}
